import React, { memo, useCallback, useMemo, useState } from 'react';
import { IDataGridFilter, IDataGridMadel } from '../../model';
import FilterString from './filter-string';
import FilterSelect from './filter-select';
import FilterDate from './filter-date';
import FilterDateMultiple from './filter-date-multiple';
import FilterSwitcher from './filter-switcher';
import { Box, BoxProps } from '@mui/material';
import clsx from 'clsx';
import { pick } from 'lodash-es';
import { FiltersButton, FiltersDialog } from 'components/filters-dialog';
import { countActiveFilters } from 'hooks/use-filters-dialog';

const FiltersList = memo(
  ({
    filtersList,
    filters,
    onTasks,
  }: {
    filtersList: IDataGridFilter[];
    filters: any;
    onTasks: any;
  }) => {
    return (
      <>
        {filtersList.map((filter) => {
          switch (filter.options?.type) {
            case 'string':
              return (
                <FilterString
                  key={filter.field}
                  filter={filter}
                  value={filters[filter.field]}
                  onTasks={onTasks}
                />
              );
            case 'select':
              return (
                <FilterSelect
                  key={filter.field}
                  filter={filter}
                  value={filters[filter.field]}
                  onTasks={onTasks}
                />
              );
            case 'date-multiple':
              return (
                <FilterDateMultiple
                  key={filter.field}
                  filter={filter}
                  value={filters[filter.field]}
                  onTasks={onTasks}
                />
              );
            case 'date':
              return (
                <FilterDate
                  key={filter.field}
                  filter={filter}
                  value={filters[filter.field]}
                  onTasks={onTasks}
                />
              );
            case 'switcher':
              return (
                <FilterSwitcher
                  key={filter.field}
                  filter={filter}
                  value={filters[filter.field]}
                  onTasks={onTasks}
                />
              );
            case 'component':
              return (
                filter.options.template && (
                  <filter.options.template
                    key={filter.field}
                    filter={filter}
                    value={filters[filter.field]}
                    onTasks={onTasks}
                  />
                )
              );
            default:
              return null;
          }
        })}
      </>
    );
  },
);

/**
 * A grid's filters as it starts (the parser's initial filters): each filter's
 * config `options.value`, '' where it has none. What "clear" puts a filters
 * dialog back to, and what its badge counts against.
 */
export const getFilterDefaults = <T extends object = Record<string, unknown>>(
  filtersList: IDataGridFilter[] = [],
) =>
  Object.fromEntries(
    filtersList.map((filter) => [filter.field, filter.options?.value ?? '']),
  ) as Partial<T>;

const isSearchFilter = (filter: IDataGridFilter) => filter.options?.type === 'string';

/**
 * N1 (brief pattern B): a card-mode grid's filters below md. The search (the
 * 'string' filters) stays at rest; every other filter is in the full-screen
 * filters dialog, behind a button whose badge counts the filters that are not
 * at their initial value (the config's `options.value`). In the dialog the
 * filters edit a draft: their tasks are caught and only the 'filters' payloads
 * kept, "apply" sends the draft as one filters task, "clear" puts it back to
 * the initial values, closing drops it.
 */
const FiltersMobileDialog = memo(
  ({
    filtersList,
    filters,
    onTasks,
  }: {
    filtersList: IDataGridFilter[];
    filters: any;
    onTasks: any;
  }) => {
    const searchList = useMemo(() => filtersList.filter(isSearchFilter), [filtersList]);
    const dialogList = useMemo(
      () => filtersList.filter((filter) => !isSearchFilter(filter)),
      [filtersList],
    );
    const fields = useMemo(() => dialogList.map((filter) => filter.field), [dialogList]);
    const initial = useMemo(() => getFilterDefaults(dialogList), [dialogList]);

    // null while the dialog is closed
    const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
    // stable: a filter keeps the first `onTasks` it is given
    const onDraftTasks = useCallback((tasks: any[]) => {
      tasks.forEach(([type, payload]) => {
        if (type === 'filters') setDraft((state) => ({ ...state, ...payload }));
      });
    }, []);
    const onOpen = useCallback(() => setDraft(pick(filters, fields)), [filters, fields]);
    const onClose = useCallback(() => setDraft(null), []);
    const onClear = useCallback(() => setDraft(initial), [initial]);
    const onApply = useCallback(() => {
      onTasks([['pagination', { page: 1 }], ['filters', draft], ['getData']]);
      setDraft(null);
    }, [onTasks, draft]);

    const count = useMemo(
      () => countActiveFilters(filters, initial, fields),
      [filters, initial, fields],
    );

    return (
      <>
        {searchList.length > 0 && (
          <div className="toolbar-filters__search">
            <FiltersList filtersList={searchList} filters={filters} onTasks={onTasks} />
          </div>
        )}
        {dialogList.length > 0 && (
          <>
            <FiltersButton count={count} onClick={onOpen} />
            <FiltersDialog
              open={draft !== null}
              onClose={onClose}
              onApply={onApply}
              onClear={onClear}
            >
              <FiltersList
                filtersList={dialogList}
                filters={draft ?? filters}
                onTasks={onDraftTasks}
              />
            </FiltersDialog>
          </>
        )}
      </>
    );
  },
);

interface IFilterProps extends BoxProps {
  disableMobiles: boolean;
  /** The grid renders cards (below md, unless its config opted out). */
  isCardMode?: boolean;
  /**
   * N2b (T3): below md. Every grid's filters are the N1 rest row + bottom sheet
   * there (DC-11, DC-18a) -- a grid that opted out of cards included; the
   * "סינון" popover toggle is gone.
   */
  isBelowMd?: boolean;
  model: IDataGridMadel;
  filters: any;
  onTasks: any;
}

const Filters = memo<IFilterProps>(({ model, disableMobiles, filters, onTasks, ...props }) => {
  const { isCardMode, isBelowMd, ...rest } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isToolbar = useMemo(() => model?.toolbar && model?.toolbar?.visible, [model?.toolbar]);
  const Filters = useMemo(
    () =>
      isToolbar && model?.toolbar?.filters && model?.toolbar.filters.length > 0
        ? model?.toolbar.filters
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isToolbar],
  );

  const FiltersElement = Filters ? (
    <FiltersList filtersList={Filters} filters={filters} onTasks={onTasks} />
  ) : null;

  // render
  if (!isToolbar || !Filters) {
    return null;
  }

  if ((isCardMode || isBelowMd) && !disableMobiles) {
    return (
      <Box {...rest} className={clsx('toolbar-filters', rest.className)}>
        <FiltersMobileDialog filtersList={Filters} filters={filters} onTasks={onTasks} />
      </Box>
    );
  }

  return (
    <Box {...rest} className={clsx('toolbar-filters', rest.className)}>
      {FiltersElement}
    </Box>
  );
});

export default Filters;
